import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useHlsPlayer, isHlsSource } from "../../../hooks/useHlsPlayer";
import type { ProfileHeader } from "../../../types/profile";
import { getMediaTypeFromUrl } from "../utils";

export interface ProfileKeyVisualProps {
  header: ProfileHeader;
}

// 旧 Django（_header.html / header.css）に合わせ、ビューポート縦いっぱいの
// フルスクリーン表示にする。モバイルの URL バーで欠けないよう svh を使う。
const FullScreenHero = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
  background-color: var(--soremo-background, #f0f0f0);
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// 全面背景動画。中間ラッパーで高さが伸びない MediaPreview ではなく、旧 Django の
// autoplay/muted/loop 背景動画と同じく素の <video> を object-fit: cover で敷く。
const HeroVideoEl = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/** 背景動画（HLS 対応）。Lambda 変換後の .m3u8 は hls.js / ネイティブ HLS で再生する。 */
const HeroVideo: React.FC<{ src: string }> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isHls = isHlsSource(src);
  useHlsPlayer(src, videoRef, isHls);

  return (
    <HeroVideoEl
      ref={videoRef}
      src={isHls ? undefined : src}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
    />
  );
};

// 旧 Django は `@media (max-aspect-ratio: 4/5)` で SP 素材へ切り替える
// （デバイス判定ではなくビューポートのアスペクト比基準）。
const PORTRAIT_QUERY = "(max-aspect-ratio: 4/5)";

/** ビューポートが縦長（SP 素材を使う条件）かどうかを返す。リサイズに追従する。 */
const useIsPortrait = (): boolean => {
  const [isPortrait, setIsPortrait] = useState<boolean>(() =>
    typeof window !== "undefined" && "matchMedia" in window
      ? window.matchMedia(PORTRAIT_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return;
    const mql = window.matchMedia(PORTRAIT_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    setIsPortrait(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isPortrait;
};

const BannerWrap = styled.section`
  width: 100%;
  aspect-ratio: 2.35 / 1;
  overflow: hidden;
  background-color: var(--soremo-background, #f0f0f0);

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/**
 * キービジュアル（ヒーロー）セクション。
 *
 * - full_screen: 画面いっぱいの背景（画像 or 動画）。表示名 / タイトルは
 *   メディアにオーバーレイせず、下の見出しセクション（`Profile.tsx`）に一本化する。
 * - banner: シネスコープ（2.35:1）固定配置。
 *
 * ビューポートのアスペクト比で PC / SP のキービジュアルを出し分ける。縦長
 * （`max-aspect-ratio: 4/5`）では SP 素材を優先し、無ければ PC にフォールバック。
 * 横長では PC を優先し、無ければ SP にフォールバックする（旧 Django 準拠）。
 */
const ProfileKeyVisual: React.FC<ProfileKeyVisualProps> = ({ header }) => {
  const isPortrait = useIsPortrait();

  if (header.type_header === "banner") {
    const src = header.banner_url;
    if (!src) return null;
    const mediaType = getMediaTypeFromUrl(src);
    return (
      <BannerWrap>
        {mediaType === "video" ? (
          <HeroVideo src={src} />
        ) : (
          <img src={src} alt="" />
        )}
      </BannerWrap>
    );
  }

  const src = isPortrait
    ? header.key_visual_sp_url || header.key_visual_pc_url
    : header.key_visual_pc_url || header.key_visual_sp_url;
  const mediaType = getMediaTypeFromUrl(src);

  return (
    <FullScreenHero>
      {src &&
        (mediaType === "video" ? (
          <HeroVideo src={src} />
        ) : (
          <HeroImage src={src} alt="" />
        ))}
    </FullScreenHero>
  );
};

export default ProfileKeyVisual;
